<script setup lang="ts">
import AlertComponent from "@/components/AlertComponent.vue";
import HasPermission from "@/components/HasPermission.vue";
import LoadingModal from "@/components/LoadingModal.vue";
import ModalComponent from "@/components/ModalComponent.vue";
import ShadowBox from "@/components/ShadowBox.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledCheckbox from "@/components/StyledCheckbox.vue";
import StyledCombobox from "@/components/StyledCombobox.vue";
import useHasPermissionUser from "@/composables/useHasPermissionUser";
import { Brand } from "@/internal/modules/providers/interfaces/Brand";
import { Chain } from "@/internal/modules/providers/interfaces/Chain";
import PaginateNav from "@/modules/trips/components/PaginateNav.vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, reactive, ref } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import useBrandForm from "../../brands/composables/useBrandForm";
import useBrands from "../../brands/composables/useBrands";
import useSearchBrands from "../../brands/composables/useSearchBrands";
import useChainBrands from "../composables/useChainBrands";

interface Props {
  chain: Chain;
}

type Form = {
  brands: Brand[];
  chain: number;
};

const route = useRoute();
const props = defineProps<Props>();
const toast = useToast();

const { data, isLoading, page, refetch, chainBrands } = useChainBrands({
  immediate: true,
  params: {
    id: route.params.id as string,
  },
});

const handlePage = (newPage: number | string) => {
  const pageNumber = Number(newPage);

  if (pageNumber >= 1 && pageNumber <= data.value?.meta.lastPage!) {
    page.value = pageNumber;
    refetch();
  }
};

const formBrands = reactive<Form>({
  brands: [],
  chain: Number(route.params.id),
});

const { hasPermissionUser } = useHasPermissionUser();

const queryClient = useQueryClient();

const { actionModal, modalAction } = useBrandForm();

const {
  data: brandsSearches,
  setQuery,
  isLoading: loadingBrands,
} = useSearchBrands({
  immediate: true,
  errorHandling: false,
});

const { updateMutationAsync, updateMutationLoading } = useBrands({
  immediate: false,
});

const selectedBrand = reactive<{
  chainId: number | null;
  name?: string;
}>({
  chainId: null,
  name: "",
});

const alert = ref(false);
const alertMultiped = ref(false);

const handleDeleteChainOfBrand = async () => {
  await updateMutationAsync({
    id: selectedBrand?.chainId!,
    chainId: null,
  });

  await queryClient.invalidateQueries(["chain-brands"]);
  alert.value = false;
};

const handleUpdateBrand = async () => {
  await Promise.all(
    formBrands.brands.map(async (brand) => {
      await updateMutationAsync({
        id: brand.id,
        chainId: formBrands.chain,
      });
    }),
  );

  await queryClient.invalidateQueries(["chain-brands"]);
  actionModal();
  formBrands.brands = [];
};

const filteredBrands = computed(() => {
  return brandsSearches.value?.data.filter((brand) => {
    return brand.chainId !== formBrands.chain;
  });
});

const filterBrand = (brand: Brand) => {
  formBrands.brands = formBrands.brands.filter((b) => b.id !== brand.id);
};

const filteredBrandsChecked = computed(() => {
  return chainBrands.value.filter((brand) => brand.checked);
});

const selectedAll = () => {
  chainBrands.value.forEach((brand) => {
    brand.checked = !brand.checked;
  });
};

const handleDeleteMassive = async () => {
  if (!filteredBrandsChecked.value.length) {
    toast.error("No hay marcas seleccionadas");
    return;
  }

  await Promise.all(
    filteredBrandsChecked.value.map(async (brand) => {
      await updateMutationAsync({
        id: brand.id,
        chainId: null,
      });
    }),
  );

  await refetch();
  alertMultiped.value = false;
};
</script>

<template>
  <ShadowBox class="bg-white">
    <div class="flex justify-between">
      <h1 class="font-bold text-2xl mb-2">Listado de las marcas</h1>

      <StyledButton
        v-if="
          hasPermissionUser('viewAny-internal', 'App\\Models\\Brand') &&
          hasPermissionUser('update-internal', 'App\\Models\\Brand')
        "
        class="bg-blue-500 text-white"
        @click="
          () => {
            actionModal();
          }
        "
      >
        <span class="material-symbols-outlined"> add </span>
        Agregar marca
      </StyledButton>
    </div>

    <ul class="" v-if="!isLoading">
      <div v-if="chainBrands">
        <!-- buttons for eliminate all brands or select the brand to eliminate -->

        <HasPermission
          :name-permission="'update-internal'"
          :entity-type="'App\\Models\\Brand'"
        >
          <div
            class="flex items-center mb-2 cursor-pointer"
            @click="selectedAll()"
            v-if="chainBrands.length > 0"
          >
            <span class="material-symbols-outlined"> done_all </span>
            <p>Seleccionar todos</p>
          </div>
        </HasPermission>

        <li v-for="brand in chainBrands" :key="brand.id" class="">
          <div class="flex items-center">
            <!-- Checkbox for select brand -->

            <HasPermission
              :name-permission="'update-internal'"
              :entity-type="'App\\Models\\Brand'"
            >
              <StyledCheckbox
                v-model="brand.checked"
                @update:model-value="
                  (value) => {
                    brand.checked = value;
                  }
                "
              />
            </HasPermission>

            <!-- Lin for edit brand -->
            <p class="flex items-center gap-2 p-2">
              <span class="material-symbols-outlined"> branding_watermark</span>
              <RouterLink
                :to="{ name: 'hotel.brands.edit', params: { id: brand.id } }"
                class="flex items-center"
                :class="
                  hasPermissionUser('viewAny-internal', 'App\\Models\\Brand')
                    ? 'cursor-pointer underline hover:text-blue-700'
                    : 'cursor-default pointer-events-none no-underline'
                "
              >
                <p class="uppercase">{{ brand.name }}</p>
              </RouterLink>
            </p>
          </div>
        </li>
      </div>
    </ul>

    <!-- Action to eliminate massive brands -->
    <div
      class="flex gap-5 items-center justify-end pt-2"
      v-if="filteredBrandsChecked.length > 0"
    >
      <StyledButton
        class="bg-red-500 text-white hover:bg-red-700"
        @click="alertMultiped = true"
      >
        <span class="material-symbols-outlined"> delete </span>
        Eliminar marcas seleccionadas
      </StyledButton>

      <StyledButton
        class="bg-blue-500 text-white"
        @click="
          () => {
            selectedAll();
          }
        "
      >
        <span class="material-symbols-outlined"> undo </span>
        Deshacer selección
      </StyledButton>
    </div>

    <div class="flex justify-end items-center">
      <PaginateNav
        :page="page"
        :pagination="data"
        :data="data?.data || []"
        :handle-page="handlePage"
      />
    </div>
  </ShadowBox>

  <!-- Alert to eliminate massive brands -->
  <AlertComponent
    v-model:model-value="alertMultiped"
    :type="'warning'"
    :dismiss="true"
    :on-confirm="handleDeleteMassive"
    :on-cancel="() => (alertMultiped = false)"
  >
    <template #title>
      Estas seguro de eliminar las marcas
      {{ filteredBrandsChecked.length > 1 ? "seleccionadas" : "seleccionada" }}:
      {{ filteredBrandsChecked.length }}
    </template>

    <template #body>
      <p v-for="brands in filteredBrandsChecked" :key="brands.id">
        {{ brands.name }}
      </p>
    </template>

    <template #closeButton> Cancelar </template>

    <template #button> Eliminar </template>
  </AlertComponent>

  <!-- Modal create brands -->
  <ModalComponent
    :model-value="modalAction"
    @update:model-value="
      () => {
        actionModal();
      }
    "
  >
    <template #modalBody>
      <div class="p-5">
        <form @submit.prevent="handleUpdateBrand">
          <h2 v-if="formBrands.brands.length > 1" class="font-bold mb-2">
            Hoteles a ligar en el grupo {{ chain.name }}
          </h2>
          <div v-for="brand in formBrands.brands" :key="brand.id">
            <div class="flex mb-1">
              <p
                class="p-2 bg-slate-400 rounded-md text-white hover:bg-slate-500 transition-colors flex items-center gap-2"
              >
                {{ brand.name }}

                <span
                  class="material-symbols-outlined text-red-500 cursor-pointer"
                  v-tooltip="'Eliminar del grupo'"
                  @click="
                    () => {
                      filterBrand(brand);
                    }
                  "
                  >delete</span
                >
              </p>
            </div>
          </div>

          <StyledCombobox
            v-model="formBrands.brands"
            multiple
            class="flex-1 mb-3"
            :options="filteredBrands || []"
            :loading-options="loadingBrands"
            label="Buscar marcas"
            :on-input-change="setQuery"
          >
            <template #default="{ option }">
              <span>{{ option.name }}</span>
            </template>
          </StyledCombobox>

          <StyledButton
            type="submit"
            class="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded ml-auto"
            :loading="updateMutationLoading"
          >
            Ligar marcas
          </StyledButton>
        </form>
      </div>
    </template>
  </ModalComponent>

  <LoadingModal :when="isLoading" />
</template>
